import React from "react";
import { AppearanceButton } from "../../Appearance";
import { normalizeExternalUrl } from "../../Helper/urls";

/**
 * Format currency for funding display.
 * @param {number|null|undefined} amount
 * @param {string} currency
 */
export function formatProjectMoney(amount, currency = "USD") {
  if (amount === null || amount === undefined || Number.isNaN(Number(amount))) {
    return "—";
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch (e) {
    return `${currency || "USD"} ${Number(amount).toFixed(0)}`;
  }
}

/**
 * Funding thermometer + support CTA for public Project pages.
 */
function ProjectFundingPanel({ funding, projectName = "Project" }) {
  if (!funding || (funding.goal_amount == null && !funding.amount_raised)) {
    return null;
  }

  const currency = funding.currency || "USD";
  const goal = funding.goal_amount != null ? Number(funding.goal_amount) : null;
  const raised = Number(funding.amount_raised || 0);
  const percent =
    funding.percent != null
      ? Math.max(0, Math.min(100, Number(funding.percent)))
      : goal && goal > 0
      ? Math.max(0, Math.min(100, Math.round((raised / goal) * 100)))
      : null;
  const days =
    funding.days_remaining !== null && funding.days_remaining !== undefined
      ? Number(funding.days_remaining)
      : null;

  const externalDonate = funding.donation_url
    ? normalizeExternalUrl(funding.donation_url)
    : "";
  const supportPath = funding.support_path || "";

  return (
    <section
      className="project-funding"
      aria-labelledby="project-funding-heading"
    >
      <h2 id="project-funding-heading" className="org-videos-heading">
        Fundraising
      </h2>
      <div className="project-funding__stats">
        <div>
          <strong>{formatProjectMoney(raised, currency)}</strong>
          <span>raised</span>
        </div>
        {goal != null ? (
          <div>
            <strong>{formatProjectMoney(goal, currency)}</strong>
            <span>goal</span>
          </div>
        ) : null}
        {percent != null ? (
          <div>
            <strong>{percent}%</strong>
            <span>funded</span>
          </div>
        ) : null}
        {days != null ? (
          <div>
            <strong>{days}</strong>
            <span>{days === 1 ? "day left" : "days left"}</span>
          </div>
        ) : null}
      </div>
      {percent != null ? (
        <div
          className="project-funding__bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={percent}
          aria-label={`${percent} percent funded`}
        >
          <span style={{ width: `${percent}%` }} />
        </div>
      ) : null}
      <div className="project-funding__actions">
        {externalDonate ? (
          <AppearanceButton
            href={externalDonate}
            variant="donate"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Support ${projectName}`}
          >
            Support this project
          </AppearanceButton>
        ) : supportPath ? (
          <AppearanceButton
            to={supportPath}
            variant="donate"
            aria-label={`Support ${projectName}`}
          >
            Support this project
          </AppearanceButton>
        ) : null}
      </div>
    </section>
  );
}

export default ProjectFundingPanel;
